import { useCallback, useEffect, useRef, useState } from 'react'
import { ChevronDown } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { FORM_FILTER_HOVER_CLASS } from '@/components/ui/form-control-styles'
import { cn } from '@/lib/utils'

export interface MultiSelectOption {
  value: string
  label: string
}

interface Props {
  options: MultiSelectOption[]
  // Selected option values (controlled).
  selected: string[]
  onChange: (values: string[]) => void
  placeholder: string
  // Trigger label once at least one option is picked, e.g. "2 selected".
  selectedLabel: (count: number) => string
  clearLabel: string
  emptyLabel: string
  triggerClass?: string
  contentClass?: string
  open?: boolean
  onOpenChange?: (open: boolean) => void
}

// Multi-select filter over a fixed, already-loaded option set (statuses, service
// categories, …) — button + popover with checkboxes, "N selected" trigger label
// and a clear action. Same UX as ClientMultiSelectFilter, without the debounced
// server search that one needs for the unbounded client list.
export function MultiSelectFilter({
  options,
  selected,
  onChange,
  placeholder,
  selectedLabel,
  clearLabel,
  emptyLabel,
  triggerClass,
  contentClass,
  open: controlledOpen,
  onOpenChange,
}: Props) {
  const [internalOpen, setInternalOpen] = useState(false)
  const open = controlledOpen ?? internalOpen
  const ref = useRef<HTMLDivElement>(null)
  const openRef = useRef(open)

  const setOpen = useCallback(
    (nextOpen: boolean) => {
      if (controlledOpen === undefined) setInternalOpen(nextOpen)
      onOpenChange?.(nextOpen)
    },
    [controlledOpen, onOpenChange]
  )

  useEffect(() => {
    openRef.current = open
  }, [open])

  useEffect(() => {
    function onDown(e: MouseEvent) {
      if (!openRef.current) return
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    function onKey(e: KeyboardEvent) {
      if (openRef.current && e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [setOpen])

  const toggle = (value: string) =>
    onChange(
      selected.includes(value)
        ? selected.filter(v => v !== value)
        : [...selected, value]
    )

  const label =
    selected.length === 0 ? placeholder : selectedLabel(selected.length)

  return (
    <div ref={ref} className='relative shrink-0'>
      <Button
        type='button'
        variant='filter'
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        className={cn(
          triggerClass,
          FORM_FILTER_HOVER_CLASS,
          selected.length > 0 && 'border-primary text-primary'
        )}
      >
        <span className='truncate'>{label}</span>
        <ChevronDown className='ml-2 size-4 shrink-0 opacity-60' />
      </Button>

      {open && (
        <div
          className={cn(
            'absolute left-0 z-50 mt-1 w-[240px] rounded-md border border-border bg-popover p-2 text-popover-foreground shadow-md',
            contentClass
          )}
        >
          {selected.length > 0 && (
            <button
              type='button'
              onClick={() => onChange([])}
              className='mb-1 w-full rounded px-2 py-1 text-left text-xs text-primary hover:bg-accent'
            >
              {clearLabel}
            </button>
          )}

          <div className='max-h-64 overflow-y-auto'>
            {options.length === 0 ? (
              <p className='px-2 py-3 text-center text-sm text-muted-foreground'>
                {emptyLabel}
              </p>
            ) : (
              options.map(o => (
                <label
                  key={o.value}
                  className='flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-accent'
                >
                  <Checkbox
                    checked={selected.includes(o.value)}
                    onCheckedChange={() => toggle(o.value)}
                  />
                  <span className='truncate'>{o.label}</span>
                </label>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default MultiSelectFilter
